<!--
  EnterCode — ввод кода из письма/SMS. Общий шаг для регистрации (SignUp) и
  сброса пароля (ResetPassword): раньше вёрстка кода дублировалась в обеих
  страницах, здесь сведена на канон. Разный текст — через пропсы (isEmail).

  Четыре отдельных инпута с автопереходом, вставкой всего кода и удалением на
  Backspace. Кнопки «Подтвердить» нет: когда заполнена последняя ячейка, шаг
  подтверждается сам (submit). Повторная отправка — таймером ResendCode.
-->
<template>
  <div>
    <p class="text mn-b-medium opacity-50">
      {{ isEmail ? t('auth.enterCode.instructionsEmail') : t('auth.enterCode.instructions') }}
      <span class="t-secondary d-block mn-t-thin">{{ identifier }}</span>
    </p>

    <div class="d-flex gap-small mn-b-medium">
      <input
        v-for="(d, i) in digits"
        :key="i"
        :ref="el => (inputs[i] = el)"
        v-model="digits[i]"
        type="text"
        inputmode="numeric"
        pattern="[0-9]*"
        maxlength="1"
        class="enter-code__cell input t-center"
        autocomplete="one-time-code"
        @input="onInput(i)"
        @keydown="onKeydown($event, i)"
        @paste="onPaste($event, i)"
        @focus="$event.target.select()"
      >
    </div>

    <div class="d-flex mn-b-big">
      <ResendCode :submit="resend" :cooldown="cooldown" />
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch, nextTick, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import ResendCode from '@martyrs/src/modules/auth/views/components/blocks/ResendCode.vue'

const { t } = useI18n({ useScope: 'global' })

const props = defineProps({
  modelValue: { type: String, default: '' },
  identifier: { type: String, default: '' },
  isEmail: { type: Boolean, default: false },
  // Повторная отправка кода — новый challenge.
  resend: { type: Function, required: true },
  // Вызывается, когда код введён целиком (авто-подтверждение, без кнопки).
  submit: { type: Function, required: true },
  cooldown: { type: Number, default: 30 },
  length: { type: Number, default: 4 }
})

const emit = defineEmits(['update:modelValue'])

const digits = reactive(Array.from({ length: props.length }, () => ''))
const inputs = ref([])

const join = () => digits.join('')

// Синхронизация наружу + авто-подтверждение по заполнении.
watch(digits, () => {
  const code = join()
  emit('update:modelValue', code)
  // Полнота кода — по ячейкам: у строки `includes('')` всегда true.
  if (digits.every(d => d !== '')) {
    // Даём v-model родителя обновиться до вызова submit (он читает code.value).
    // Ошибку валидации родителя гасим — юзер остаётся на шаге кода.
    nextTick(() => { try { Promise.resolve(props.submit()).catch(() => {}) } catch (e) {} })
  }
})

// Родитель обнулил код (новая отправка) — чистим ячейки и возвращаем фокус.
watch(() => props.modelValue, (val) => {
  if (val === '' && join() !== '') {
    for (let i = 0; i < digits.length; i++) digits[i] = ''
    focus(0)
  }
})

function focus(i) {
  const el = inputs.value[i]
  if (el) { el.focus(); el.select?.() }
}

function onInput(i) {
  // Оставляем одну цифру.
  digits[i] = String(digits[i] || '').replace(/\D/g, '').slice(-1)
  if (digits[i] && i < digits.length - 1) focus(i + 1)
}

function onKeydown(e, i) {
  if (e.key === 'Backspace' && !digits[i] && i > 0) {
    e.preventDefault()
    digits[i - 1] = ''
    focus(i - 1)
  } else if (e.key === 'ArrowLeft' && i > 0) {
    e.preventDefault(); focus(i - 1)
  } else if (e.key === 'ArrowRight' && i < digits.length - 1) {
    e.preventDefault(); focus(i + 1)
  } else if (!/^\d$/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key) && !e.ctrlKey && !e.metaKey) {
    e.preventDefault()
  }
}

// Вставка всего кода из письма — раскладываем по ячейкам с текущей позиции.
function onPaste(e, i) {
  e.preventDefault()
  const chars = (e.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '').split('')
  if (!chars.length) return
  let pos = i
  for (const ch of chars) {
    if (pos >= digits.length) break
    digits[pos] = ch
    pos++
  }
  focus(Math.min(pos, digits.length - 1))
}

onMounted(() => focus(0))
</script>

<style scoped>
.enter-code__cell {
  flex: 1 1 0;
  min-width: 0;
  height: 3.5rem;
  padding: 0;
  font-size: 1.5rem;
  text-align: center;
}
</style>
